import { Dispatch, Action, ActionCreator } from "redux";
import { ThunkAction } from "redux-thunk";

import { GlobalState } from "@/Reducers/interface";
import {
	Report,
	ReportConfig,
	CreateReportFormState,
	ChartType,
	ReportFilter,
	CreateReportFormStateInterface,
	GroupByField,
	ReportsListRequest
} from "@/Contracts/Reports/Reports.contract";
import {
	getReports,
	getReportDetails,
	getCreateReportConfig,
	createReportPreview,
	createReport,
	editReport,
	deleteReport
} from "../../Services/Reports/Reports.service";
import { GlobalSearchEntityType } from "@/Contracts/Others/GlobalSearch";
import { ValidationError } from "../../Components/Utils/ServerError";

export enum ReportsActionTypes {
	SHOW_REPORTS_LOADER = "SHOW_REPORTS_LOADER",
	HIDE_REPORTS_LOADER = "HIDE_REPORTS_LOADER",

	GET_REPORTS_LIST_REQUESTED = "GET_REPORTS_LIST_REQUESTED",
	GET_REPORTS_LIST_SUCCESSFUL = "GET_REPORTS_LIST_SUCCESSFUL",
	GET_REPORTS_LIST_FAILED = "GET_REPORTS_LIST_FAILED",

	GET_REPORTS_DETAILS_REQUESTED = "GET_REPORTS_DETAILS_REQUESTED",
	GET_REPORTS_DETAILS_SUCCESSFUL = "GET_REPORTS_DETAILS_SUCCESSFUL",
	GET_REPORTS_DETAILS_FAILED = "GET_REPORTS_DETAILS_FAILED",
	RESET_REPORTS_DETAILS = "RESET_REPORTS_DETAILS",

	SET_REPORT_DETAILS = "SET_REPORT_DETAILS",

	GET_REPORTS_CONFIG_FAILED = "GET_REPORTS_CONFIG_FAILED",
	GET_REPORTS_CONFIG_REQUESTED = "GET_REPORTS_CONFIG_REQUESTED",
	GET_REPORTS_CONFIG_SUCCESSFUL = "GET_REPORTS_CONFIG_SUCCESSFUL",

	CREATE_REPORT_PREVIEW_FAILED = "CREATE_REPORT_PREVIEW_FAILED",
	CREATE_REPORT_PREVIEW_REQUESTED = "CREATE_REPORT_PREVIEW_REQUESTED",
	CREATE_REPORT_PREVIEW_SUCCESSFUL = "CREATE_REPORT_PREVIEW_SUCCESSFUL",

	RESET_CREATE_REPORT_FORM = "RESET_CREATE_REPORT_FORM",

	CREATE_REPORT_FAILED = "CREATE_REPORT_FAILED",
	CREATE_REPORT_REQUESTED = "CREATE_REPORT_REQUESTED",
	CREATE_REPORT_SUCCESSFUL = "CREATE_REPORT_SUCCESSFUL",

	UPDATE_REPORT_FAILED = "UPDATE_REPORT_FAILED",
	UPDATE_REPORT_REQUESTED = "UPDATE_REPORT_REQUESTED",
	UPDATE_REPORT_SUCCESSFUL = "UPDATE_REPORT_SUCCESSFUL",

	UPDATE_REPORT_NAME_FAILED = "UPDATE_REPORT_NAME_FAILED",
	UPDATE_REPORT_NAME_REQUESTED = "UPDATE_REPORT_NAME_REQUESTED",
	UPDATE_REPORT_NAME_SUCCESSFUL = "UPDATE_REPORT_NAME_SUCCESSFUL",

	DELETE_REPORT_FAILED = "DELETE_REPORT_FAILED",
	DELETE_REPORT_REQUESTED = "DELETE_REPORT_REQUESTED",
	DELETE_REPORT_SUCCESSFUL = "DELETE_REPORT_SUCCESSFUL"
}

export type ShowReportsLoaderAction =
	Action<ReportsActionTypes.SHOW_REPORTS_LOADER>;
export type HideReportsLoaderAction =
	Action<ReportsActionTypes.HIDE_REPORTS_LOADER>;
export type GetReportsListRequestedAction =
	Action<ReportsActionTypes.GET_REPORTS_LIST_REQUESTED>;
export interface GetReportsListSuccessfulAction
	extends Action<ReportsActionTypes.GET_REPORTS_LIST_SUCCESSFUL> {
	readonly reportsList: Report[];
	readonly reportsCountMap?: { all: number };
}
export type GetReportsListFailedAction =
	Action<ReportsActionTypes.GET_REPORTS_LIST_FAILED>;
export type GetReportsDetailsRequestedAction =
	Action<ReportsActionTypes.GET_REPORTS_DETAILS_REQUESTED>;
export interface GetReportsDetailsSuccessfulAction
	extends Action<ReportsActionTypes.GET_REPORTS_DETAILS_SUCCESSFUL> {
	readonly currentActiveReport: Report;
}
export type GetReportsDetailsFailedAction =
	Action<ReportsActionTypes.GET_REPORTS_DETAILS_FAILED>;

export type ResetReportsDetailsAction =
	Action<ReportsActionTypes.RESET_REPORTS_DETAILS>;

export type GetReportsConfigFailedAction =
	Action<ReportsActionTypes.GET_REPORTS_CONFIG_FAILED>;
export type GetReportsConfigRequestedAction =
	Action<ReportsActionTypes.GET_REPORTS_CONFIG_REQUESTED>;
export interface GetReportsConfigSuccessfulAction
	extends Action<ReportsActionTypes.GET_REPORTS_CONFIG_SUCCESSFUL> {
	readonly reportConfig: ReportConfig;
}

export interface SetReportDetailsAction
	extends Action<ReportsActionTypes.SET_REPORT_DETAILS> {
	report: Report;
}

export interface CreateReportPreviewFailedAction
	extends Action<ReportsActionTypes.CREATE_REPORT_PREVIEW_FAILED> {
	errorMessage: string | null;
}
export interface CreateReportPreviewRequestedAction
	extends Action<ReportsActionTypes.CREATE_REPORT_PREVIEW_REQUESTED> {
	createReportForm: CreateReportFormState;
}
export interface CreateReportPreviewSuccessfulAction
	extends Action<ReportsActionTypes.CREATE_REPORT_PREVIEW_SUCCESSFUL> {
	readonly currentActiveReport: Report | null;
}

export type ResetCreateReportFormAction =
	Action<ReportsActionTypes.RESET_CREATE_REPORT_FORM>;

export type CreateReportFailedAction =
	Action<ReportsActionTypes.CREATE_REPORT_FAILED>;
export type CreateReportRequestedAction =
	Action<ReportsActionTypes.CREATE_REPORT_REQUESTED>;
export interface CreateReportSuccessfulAction
	extends Action<ReportsActionTypes.CREATE_REPORT_SUCCESSFUL> {
	readonly report: Report;
}

export interface UpdateReportFailedAction
	extends Action<ReportsActionTypes.UPDATE_REPORT_FAILED> {
	errorMessage?: string;
}
export type UpdateReportRequestedAction =
	Action<ReportsActionTypes.UPDATE_REPORT_REQUESTED>;
export interface UpdateReportSuccessfulAction
	extends Action<ReportsActionTypes.UPDATE_REPORT_SUCCESSFUL> {
	readonly report: Report;
}

export type DeleteReportFailedAction =
	Action<ReportsActionTypes.DELETE_REPORT_FAILED>;
export type DeleteReportRequestedAction =
	Action<ReportsActionTypes.DELETE_REPORT_REQUESTED>;
export type DeleteReportSuccessfulAction =
	Action<ReportsActionTypes.DELETE_REPORT_SUCCESSFUL>;

export type UpdateReportNameFailedAction =
	Action<ReportsActionTypes.UPDATE_REPORT_NAME_FAILED>;
export type UpdateReportNameRequestedAction =
	Action<ReportsActionTypes.UPDATE_REPORT_NAME_REQUESTED>;
export interface UpdateReportNameSuccessfulAction
	extends Action<ReportsActionTypes.UPDATE_REPORT_NAME_SUCCESSFUL> {
	reportName: string;
}

export type ReportsActions =
	| ShowReportsLoaderAction
	| HideReportsLoaderAction
	| GetReportsListRequestedAction
	| GetReportsListSuccessfulAction
	| GetReportsListFailedAction
	| GetReportsDetailsRequestedAction
	| GetReportsDetailsSuccessfulAction
	| GetReportsDetailsFailedAction
	| ResetReportsDetailsAction
	| GetReportsConfigFailedAction
	| GetReportsConfigRequestedAction
	| GetReportsConfigSuccessfulAction
	| SetReportDetailsAction
	| CreateReportPreviewFailedAction
	| CreateReportPreviewRequestedAction
	| CreateReportPreviewSuccessfulAction
	| ResetCreateReportFormAction
	| CreateReportFailedAction
	| CreateReportRequestedAction
	| CreateReportSuccessfulAction
	| UpdateReportFailedAction
	| UpdateReportRequestedAction
	| UpdateReportSuccessfulAction
	| DeleteReportFailedAction
	| DeleteReportRequestedAction
	| DeleteReportSuccessfulAction
	| UpdateReportNameFailedAction
	| UpdateReportNameRequestedAction
	| UpdateReportNameSuccessfulAction;

export interface ReportsDispatch {
	showReportsLoader: ActionCreator<
		ThunkAction<
			ShowReportsLoaderAction,
			GlobalState,
			null,
			ShowReportsLoaderAction
		>
	>;
	hideReportsLoader: ActionCreator<
		ThunkAction<
			HideReportsLoaderAction,
			GlobalState,
			null,
			HideReportsLoaderAction
		>
	>;
	createReport: ActionCreator<
		ThunkAction<
			Promise<CreateReportSuccessfulAction>,
			GlobalState,
			null,
			CreateReportSuccessfulAction
		>
	>;
	cloneReport: ActionCreator<
		ThunkAction<
			Promise<CreateReportSuccessfulAction>,
			GlobalState,
			Report,
			CreateReportSuccessfulAction
		>
	>;
	cloneReportFromForm: ActionCreator<
		ThunkAction<
			Promise<CreateReportSuccessfulAction>,
			GlobalState,
			string,
			CreateReportSuccessfulAction
		>
	>;
	deleteReport: ActionCreator<
		ThunkAction<
			Promise<DeleteReportSuccessfulAction>,
			GlobalState,
			string,
			DeleteReportSuccessfulAction
		>
	>;
	updateReportFromActiveForm: ActionCreator<
		ThunkAction<
			Promise<UpdateReportSuccessfulAction>,
			GlobalState,
			string,
			UpdateReportSuccessfulAction
		>
	>;
	updateReportName: ActionCreator<
		ThunkAction<
			Promise<UpdateReportNameSuccessfulAction>,
			GlobalState,
			string,
			UpdateReportNameSuccessfulAction
		>
	>;
	getReportsDetails: ActionCreator<
		ThunkAction<
			Promise<GetReportsDetailsSuccessfulAction>,
			GlobalState,
			{ reportId: string },
			GetReportsDetailsSuccessfulAction
		>
	>;
	resetReportsDetails: ActionCreator<
		ThunkAction<
			ResetReportsDetailsAction,
			GlobalState,
			null,
			ResetReportsDetailsAction
		>
	>;
	getReportsList: ActionCreator<
		ThunkAction<
			Promise<GetReportsListSuccessfulAction>,
			GlobalState,
			ReportsListRequest,
			GetReportsListSuccessfulAction
		>
	>;
	getReportsConfig: ActionCreator<
		ThunkAction<
			Promise<GetReportsConfigSuccessfulAction>,
			GlobalState,
			null,
			GetReportsConfigSuccessfulAction
		>
	>;
	updateCreateFormData: ActionCreator<
		ThunkAction<
			Promise<CreateReportPreviewSuccessfulAction>,
			GlobalState,
			Partial<CreateReportFormState>,
			CreateReportPreviewSuccessfulAction
		>
	>;
	setReportDetails: ActionCreator<
		ThunkAction<
			SetReportDetailsAction,
			GlobalState,
			{ report: Report },
			SetReportDetailsAction
		>
	>;
	resetCreateReportForm: ActionCreator<
		ThunkAction<
			ResetCreateReportFormAction,
			GlobalState,
			null,
			ResetCreateReportFormAction
		>
	>;
}

export const ReportsActionCreators: ReportsDispatch = {
	showReportsLoader:
		() =>
		(dispatch: Dispatch): ShowReportsLoaderAction => {
			const showReportsLoaderAction: ShowReportsLoaderAction = {
				type: ReportsActionTypes.SHOW_REPORTS_LOADER
			};
			return dispatch(showReportsLoaderAction);
		},
	hideReportsLoader:
		() =>
		(dispatch: Dispatch): HideReportsLoaderAction => {
			const hideReportsLoaderAction: HideReportsLoaderAction = {
				type: ReportsActionTypes.HIDE_REPORTS_LOADER
			};
			return dispatch(hideReportsLoaderAction);
		},
	resetReportsDetails:
		() =>
		(dispatch: Dispatch): ResetReportsDetailsAction => {
			const resetReportsDetailsAction: ResetReportsDetailsAction = {
				type: ReportsActionTypes.RESET_REPORTS_DETAILS
			};
			return dispatch(resetReportsDetailsAction);
		},
	resetCreateReportForm:
		() =>
		(dispatch: Dispatch): ResetCreateReportFormAction => {
			const resetCreateReportFormAction: ResetCreateReportFormAction = {
				type: ReportsActionTypes.RESET_CREATE_REPORT_FORM
			};
			return dispatch(resetCreateReportFormAction);
		},
	createReport:
		() =>
		async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<CreateReportSuccessfulAction> => {
			try {
				const { createReportForm } = getState().reports;
				if (!createReportForm) {
					throw new Error("No Active Report");
				}
				const createReportRequestedAction: CreateReportRequestedAction =
					{
						type: ReportsActionTypes.CREATE_REPORT_REQUESTED
					};
				dispatch(createReportRequestedAction);

				const reportResponse = await createReport(createReportForm);

				const createReportSuccessfulAction: CreateReportSuccessfulAction =
					{
						type: ReportsActionTypes.CREATE_REPORT_SUCCESSFUL,
						report: reportResponse
					};
				return dispatch(createReportSuccessfulAction);
			} catch (error) {
				console.error(error);

				const createReportFailedAction: CreateReportFailedAction = {
					type: ReportsActionTypes.CREATE_REPORT_FAILED
				};
				dispatch(createReportFailedAction);
				throw error;
			}
		},
	deleteReport:
		(reportId: string) =>
		async (dispatch: Dispatch): Promise<DeleteReportSuccessfulAction> => {
			try {
				const deleteReportRequestedAction: DeleteReportRequestedAction =
					{
						type: ReportsActionTypes.DELETE_REPORT_REQUESTED
					};
				dispatch(deleteReportRequestedAction);

				await deleteReport(reportId);

				const deleteReportSuccessfulAction: DeleteReportSuccessfulAction =
					{
						type: ReportsActionTypes.DELETE_REPORT_SUCCESSFUL
					};
				return dispatch(deleteReportSuccessfulAction);
			} catch (error) {
				console.error(error);

				const deleteReportFailedAction: DeleteReportFailedAction = {
					type: ReportsActionTypes.DELETE_REPORT_FAILED
				};
				dispatch(deleteReportFailedAction);
				throw error;
			}
		},

	// this gets triggered from the details page,
	// cloning the current state of the report
	cloneReportFromForm:
		(report: Report) =>
		async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<CreateReportSuccessfulAction> => {
			try {
				const { createReportForm } = getState().reports;
				if (!createReportForm) {
					throw new Error("No Active Report");
				}
				const createReportRequestedAction: CreateReportRequestedAction =
					{
						type: ReportsActionTypes.CREATE_REPORT_REQUESTED
					};
				dispatch(createReportRequestedAction);
				const reportResponse = await createReport(report);

				const createReportSuccessfulAction: CreateReportSuccessfulAction =
					{
						type: ReportsActionTypes.CREATE_REPORT_SUCCESSFUL,
						report: reportResponse
					};
				return dispatch(createReportSuccessfulAction);
			} catch (error) {
				console.error(error);

				const createReportFailedAction: CreateReportFailedAction = {
					type: ReportsActionTypes.CREATE_REPORT_FAILED
				};
				dispatch(createReportFailedAction);
				throw error;
			}
		},
	cloneReport:
		(report: Report) =>
		async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<CreateReportSuccessfulAction> => {
			try {
				const { createReportForm } = getState().reports;
				if (!createReportForm) {
					throw new Error("No Active Report");
				}
				const createReportRequestedAction: CreateReportRequestedAction =
					{
						type: ReportsActionTypes.CREATE_REPORT_REQUESTED
					};
				dispatch(createReportRequestedAction);

				const reportResponse = await createReport(report);

				const createReportSuccessfulAction: CreateReportSuccessfulAction =
					{
						type: ReportsActionTypes.CREATE_REPORT_SUCCESSFUL,
						report: reportResponse
					};
				return dispatch(createReportSuccessfulAction);
			} catch (error) {
				console.error(error);

				const createReportFailedAction: CreateReportFailedAction = {
					type: ReportsActionTypes.CREATE_REPORT_FAILED
				};
				dispatch(createReportFailedAction);
				throw error;
			}
		},
	updateReportFromActiveForm:
		(reportId: string) =>
		async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<UpdateReportSuccessfulAction> => {
			const { currentActiveReport: report } = getState().reports;

			try {
				if (!report) {
					throw new Error("No Active Report");
				}

				const updateReportRequestedAction: UpdateReportRequestedAction =
					{
						type: ReportsActionTypes.UPDATE_REPORT_REQUESTED
					};
				dispatch(updateReportRequestedAction);

				const reportResponse = await editReport({
					...report,
					id: reportId
				});

				if (!reportResponse.data) {
					throw new ValidationError(
						"There seems to be some problem while fetching data from server"
					);
				}

				const updateReportSuccessfulAction: UpdateReportSuccessfulAction =
					{
						type: ReportsActionTypes.UPDATE_REPORT_SUCCESSFUL,
						report: reportResponse
					};
				return dispatch(updateReportSuccessfulAction);
			} catch (error) {
				console.error(error);

				const updateReportFailedAction: UpdateReportFailedAction = {
					type: ReportsActionTypes.UPDATE_REPORT_FAILED,
					errorMessage: error.displayMessage || null
				};
				dispatch(updateReportFailedAction);
				throw error;
			}
		},
	updateReportName:
		(data: { id: string; name: string }) =>
		async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<UpdateReportNameSuccessfulAction> => {
			const { currentActiveReport: report } = getState().reports;

			try {
				if (!report) {
					throw new Error("No Active Report");
				}

				const updateReportRequestedAction: UpdateReportNameRequestedAction =
					{
						type: ReportsActionTypes.UPDATE_REPORT_NAME_REQUESTED
					};
				dispatch(updateReportRequestedAction);

				// NOTE: to be fixed in future
				const reportResponse = await editReport({
					name: data.name,
					id: data.id
				});

				const updateReportSuccessfulAction: UpdateReportNameSuccessfulAction =
					{
						type: ReportsActionTypes.UPDATE_REPORT_NAME_SUCCESSFUL,
						reportName: reportResponse.name
					};
				return dispatch(updateReportSuccessfulAction);
			} catch (error) {
				console.error(error);

				const updateReportFailedAction: UpdateReportNameFailedAction = {
					type: ReportsActionTypes.UPDATE_REPORT_NAME_FAILED
				};
				dispatch(updateReportFailedAction);
				throw error;
			}
		},
	getReportsDetails:
		({ reportId }: { reportId: string }) =>
		async (
			dispatch: Dispatch
		): Promise<GetReportsDetailsSuccessfulAction> => {
			try {
				const getReportsDetailsRequestedAction: GetReportsDetailsRequestedAction =
					{
						type: ReportsActionTypes.GET_REPORTS_DETAILS_REQUESTED
					};
				dispatch(getReportsDetailsRequestedAction);

				const reportDetails = await getReportDetails(reportId);
				const getReportsDetailsSuccessfulAction: GetReportsDetailsSuccessfulAction =
					{
						type: ReportsActionTypes.GET_REPORTS_DETAILS_SUCCESSFUL,
						currentActiveReport: reportDetails
					};
				return dispatch(getReportsDetailsSuccessfulAction);
			} catch (error) {
				console.error(error);

				const getReportsDetailsFailedAction: GetReportsDetailsFailedAction =
					{
						type: ReportsActionTypes.GET_REPORTS_DETAILS_FAILED
					};
				dispatch(getReportsDetailsFailedAction);
				throw error;
			}
		},
	getReportsList:
		(paginationParams?: ReportsListRequest) =>
		async (dispatch: Dispatch): Promise<GetReportsListSuccessfulAction> => {
			try {
				const getReportsListRequestedAction: GetReportsListRequestedAction =
					{
						type: ReportsActionTypes.GET_REPORTS_LIST_REQUESTED
					};
				dispatch(getReportsListRequestedAction);

				const reportsListResponse = await getReports(paginationParams);
				const getReportsDetailsSuccessfulAction: GetReportsListSuccessfulAction =
					{
						type: ReportsActionTypes.GET_REPORTS_LIST_SUCCESSFUL,
						reportsList: reportsListResponse.data,
						reportsCountMap: reportsListResponse.filtered_count_map
					};
				return dispatch(getReportsDetailsSuccessfulAction);
			} catch (error) {
				console.error(error);

				const GetReportsListFailedAction: GetReportsListFailedAction = {
					type: ReportsActionTypes.GET_REPORTS_LIST_FAILED
				};
				dispatch(GetReportsListFailedAction);
				throw error;
			}
		},
	getReportsConfig:
		() =>
		async (
			dispatch: Dispatch
		): Promise<GetReportsConfigSuccessfulAction> => {
			try {
				const getReportsConfigRequestedAction: GetReportsConfigRequestedAction =
					{
						type: ReportsActionTypes.GET_REPORTS_CONFIG_REQUESTED
					};
				dispatch(getReportsConfigRequestedAction);

				const reportConfigResponse = await getCreateReportConfig();
				const getReportsDetailsSuccessfulAction: GetReportsConfigSuccessfulAction =
					{
						type: ReportsActionTypes.GET_REPORTS_CONFIG_SUCCESSFUL,
						reportConfig: reportConfigResponse
					};
				return dispatch(getReportsDetailsSuccessfulAction);
			} catch (error) {
				console.error(error);

				const getReportsConfigFailedAction: GetReportsConfigFailedAction =
					{
						type: ReportsActionTypes.GET_REPORTS_CONFIG_FAILED
					};
				dispatch(getReportsConfigFailedAction);
				throw error;
			}
		},
	updateCreateFormData:
		(createReportFormUpdate: Partial<CreateReportFormState>) =>
		async (
			dispatch: Dispatch,
			getState: () => GlobalState
		): Promise<CreateReportPreviewSuccessfulAction> => {
			try {
				const { createReportForm: oldCreateReportForm, reportConfig } =
					getState().reports;
				const fullCreateReportForm: CreateReportFormState = {
					...oldCreateReportForm,
					...createReportFormUpdate,
					name:
						createReportFormUpdate.name ??
						oldCreateReportForm.name ??
						"",
					chart_type:
						createReportFormUpdate.chart_type ??
						oldCreateReportForm.chart_type ??
						ChartType.Line,
					entity_type:
						createReportFormUpdate.entity_type ??
						oldCreateReportForm.entity_type ??
						GlobalSearchEntityType.Opportunity
				};

				try {
					// check if there are any new group bys that need without a filter
					const groupBys = [
						fullCreateReportForm.group_by_0,
						fullCreateReportForm.group_by_1
					]

						// skipping  if they are not time group bys
						.filter(a => !!a?.time_accumulator)

						// skipping if they already exists
						.filter(
							a =>
								!fullCreateReportForm.filters?.find(c => {
									return (
										c.field.field_id === a?.field.field_id
									);
								})
						);

					if (!groupBys) {
						throw new Error("No valid time group bys");
					}

					// if yes, then find if there is a filter field on that same key,
					const selectedModule = reportConfig?.data.find(
						a => a.module.key === fullCreateReportForm.entity_type
					);

					if (!selectedModule) {
						throw new Error("No valid sleected module");
					}

					// if yes. then add the respective filter
					const selectedFilters: ReportFilter[] = (
						groupBys as GroupByField[]
					).map(gB => {
						const relatedFilter = selectedModule.filters.find(
							currFilter =>
								currFilter.field.field_id === gB.field.field_id
						);
						let filterOperator = "";
						switch (gB.time_accumulator?.key) {
							case "yearly": {
								filterOperator = "last_year";
								break;
							}
							case "monthly": {
								filterOperator = "last_year";
								break;
							}
							case "daily": {
								filterOperator = "last_month";
								break;
							}
							case "weekly": {
								filterOperator = "last_month";
								break;
							}
							case "quarterly": {
								filterOperator = "last_year";
								break;
							}
							default: {
								throw new Error("Not a valid time_accumulator");
							}
						}

						const reportFilter: ReportFilter = {
							field: {
								field_id: gB?.field.field_id,
								display_value: gB.field.display_value
							},
							filter_operator: {
								key: filterOperator,
								display_value:
									relatedFilter?.valid_operators?.find(
										currValue =>
											currValue.key === filterOperator
									)?.display_value || filterOperator
							},
							values: []
						};
						return reportFilter;
					});

					fullCreateReportForm.filters = [
						...selectedFilters,
						...(fullCreateReportForm.filters || [])
					];
				} catch (error) {
					console.warn(error);
				}

				const isPipelineFilterApplied =
					fullCreateReportForm.filters.some(
						f => f.field.field_id === "opportunity_pipeline"
					);

				const isGroupByOppStageApplied =
					fullCreateReportForm.group_by_0?.field.field_id ===
						"opportunity_stage" ||
					fullCreateReportForm.group_by_1?.field.field_id ===
						"opportunity_stage";

				if (!isPipelineFilterApplied && isGroupByOppStageApplied) {
					const pipeline = getState().pipelines.pipelineList.find(
						p => p.is_default
					);

					if (!pipeline) throw new Error("No default pipeline");

					const pipelineFilter = {
						field: {
							field_id: "opportunity_pipeline",
							display_value: "Opportunity Pipeline"
						},
						filter_operator: {
							key: "in",
							display_value: "in"
						},
						values: [
							{
								id_value: pipeline.id,
								display_value: "Default Pipeline"
							}
						]
					};

					fullCreateReportForm.filters.push(pipelineFilter);
				}

				const getReportsConfigRequestedAction: CreateReportPreviewRequestedAction =
					{
						type: ReportsActionTypes.CREATE_REPORT_PREVIEW_REQUESTED,
						createReportForm: fullCreateReportForm
					};
				dispatch(getReportsConfigRequestedAction);

				const validCreateReportState =
					!!fullCreateReportForm.group_by_0?.field.field_id &&
					!!fullCreateReportForm.metric_0?.field.field_id;

				if (validCreateReportState) {
					const reportPreviewResponse = await createReportPreview(
						fullCreateReportForm
					);

					const getReportsDetailsSuccessfulAction: CreateReportPreviewSuccessfulAction =
						{
							type: ReportsActionTypes.CREATE_REPORT_PREVIEW_SUCCESSFUL,
							currentActiveReport: reportPreviewResponse
						};
					return dispatch(getReportsDetailsSuccessfulAction);
				} else {
					const getReportsDetailsSuccessfulAction: CreateReportPreviewSuccessfulAction =
						{
							type: ReportsActionTypes.CREATE_REPORT_PREVIEW_SUCCESSFUL,
							currentActiveReport: null
						};
					return dispatch(getReportsDetailsSuccessfulAction);
				}
			} catch (error) {
				console.error(error);

				const getReportsConfigFailedAction: CreateReportPreviewFailedAction =
					{
						type: ReportsActionTypes.CREATE_REPORT_PREVIEW_FAILED,
						errorMessage: error.displayMessage || null
					};
				dispatch(getReportsConfigFailedAction);
				throw error;
			}
		},

	setReportDetails:
		({ report }: { report: Report }) =>
		(dispatch: Dispatch): SetReportDetailsAction => {
			const setReportDetailsAction: SetReportDetailsAction = {
				type: ReportsActionTypes.SET_REPORT_DETAILS,
				report
			};
			return dispatch(setReportDetailsAction);
		}
};

export default ReportsActionCreators;
